import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Snowflake, Clock, ArrowDownToLine, Thermometer } from "lucide-react";
import { fetchFrostHistory, fetchThresholds } from "@/lib/api";
import { RISK_COLORS, fmt } from "@/lib/risk";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const RANGES = [
    { key: 7,  label: "7 nights" },
    { key: 14, label: "14 nights" },
    { key: 30, label: "30 nights" },
    { key: 60, label: "60 nights" },
];

function fmtMinutes(m) {
    if (!m) return "0 min";
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (h === 0) return `${rem} min`;
    if (rem === 0) return `${h}h`;
    return `${h}h ${rem}m`;
}

function fmtTime(iso) {
    if (!iso) return "–";
    return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function severity(minTemp, threshold) {
    if (minTemp === undefined || minTemp === null) return "green";
    if (minTemp <= threshold - 2) return "red";
    if (minTemp <= threshold) return "orange";
    if (minTemp <= threshold + 1.5) return "yellow";
    return "green";
}

export default function FrostHistoryPage() {
    const [days, setDays] = useState(14);
    const { data, isLoading } = useQuery({
        queryKey: ["frost-history", days], queryFn: () => fetchFrostHistory(days),
    });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });

    const rows = useMemo(() => {
        if (!data) return [];
        const dates = data.dates;
        const stationIds = Object.keys(data.nights);
        return dates.map((d) => ({
            date: d,
            cells: stationIds.map((sid) => ({
                station: data.nights[sid].station,
                event: data.nights[sid].by_date[d] || null,
            })),
        }));
    }, [data]);

    if (isLoading || !data || !thr) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const stationIds = Object.keys(data.nights);
    const threshold = data.threshold_c;

    // Per-station totals across the window.
    const totals = stationIds.map((sid) => {
        const events = data.nights[sid].events;
        const minMin = events.length ? Math.min(...events.map((e) => e.min_temp)) : null;
        const nightsWithFrost = events.length;
        const totalMinutes = events.reduce((a, e) => a + e.minutes_below, 0);
        return {
            station: data.nights[sid].station,
            nights: nightsWithFrost,
            minMin,
            totalMinutes,
        };
    });

    return (
        <div className="space-y-6" data-testid="frost-history-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Frost History</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Minutes below <span className="fw-metric font-semibold text-slate-900">{threshold}°C</span> per station, per night
                    </p>
                </div>
                <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                    <TabsList className="bg-white border border-slate-200 p-1">
                        {RANGES.map((r) => (
                            <TabsTrigger key={r.key} value={String(r.key)} data-testid={`frost-range-${r.key}`}>{r.label}</TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            {/* Totals row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {totals.map((t) => (
                    <div key={t.station.id} className="fw-card p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Station</div>
                                <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{t.station.name}</div>
                            </div>
                            <Snowflake className="h-4 w-4 text-sky-500" />
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Nights</div>
                                <div className="fw-metric font-bold text-slate-900">{t.nights}</div>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Min</div>
                                <div className="fw-metric font-bold text-slate-900">
                                    {t.minMin === null ? "–" : `${fmt(t.minMin, 1)}°`}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total</div>
                                <div className="fw-metric font-bold text-slate-900 text-[13px]">{fmtMinutes(t.totalMinutes)}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Compare grid */}
            <div className="fw-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]" data-testid="frost-history-table">
                        <thead>
                            <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 bg-slate-50">
                                <th className="px-4 py-3 sticky left-0 bg-slate-50 z-10">Date</th>
                                {stationIds.map((sid) => (
                                    <th key={sid} className="px-4 py-3">{data.nights[sid].station.name}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td className="px-4 py-8 text-center text-slate-500" colSpan={stationIds.length + 1}>
                                        No frost recorded in the selected window.
                                    </td>
                                </tr>
                            )}
                            {rows.map(({ date, cells }) => (
                                <tr key={date} className="border-b border-slate-100 last:border-b-0">
                                    <td className="px-4 py-3 font-semibold text-slate-900 sticky left-0 bg-white z-10 whitespace-nowrap">
                                        {new Date(date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
                                    </td>
                                    {cells.map(({ station, event }) => (
                                        <td key={station.id} className="px-4 py-3">
                                            {event ? <FrostCell event={event} threshold={threshold} sid={station.id} /> : (
                                                <span className="text-slate-400 text-xs">—</span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Per-station event lists */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stationIds.map((sid) => (
                    <StationFrostCard key={sid} entry={data.nights[sid]} threshold={threshold} />
                ))}
            </div>
        </div>
    );
}

function FrostCell({ event, threshold, sid }) {
    const sev = severity(event.min_temp, threshold);
    const c = RISK_COLORS[sev];
    return (
        <Link
            to={`/stations/${sid}`}
            data-testid={`frost-cell-${sid}-${event.date}`}
            className="inline-flex flex-col gap-0.5 px-2.5 py-1.5 rounded-lg border hover:brightness-95 transition"
            style={{ background: c.bg, borderColor: c.border }}
        >
            <span className="fw-metric font-bold" style={{ color: c.text }}>{fmt(event.min_temp, 1)}°C</span>
            <span className="text-[11px] text-slate-700">{fmtMinutes(event.minutes_below)}</span>
            <span className="text-[10px] text-slate-500">{fmtTime(event.start_time)}–{fmtTime(event.end_time)}</span>
        </Link>
    );
}

function StationFrostCard({ entry, threshold }) {
    const events = [...entry.events].sort((a, b) => b.date.localeCompare(a.date));
    return (
        <div className="fw-card p-5" data-testid={`frost-card-${entry.station.id}`}>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Frost nights</div>
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{entry.station.name}</h3>
                </div>
                <Link to={`/stations/${entry.station.id}`} className="text-xs font-semibold text-[var(--fw-accent)]">Open →</Link>
            </div>
            {events.length === 0 && <p className="text-sm text-slate-500 py-4">No frost recorded in the selected window.</p>}
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                {events.map((e) => {
                    const sev = severity(e.min_temp, threshold);
                    const c = RISK_COLORS[sev];
                    return (
                        <div key={e.date} className="p-3 rounded-xl border flex items-center justify-between gap-3"
                             style={{ background: c.bg, borderColor: c.border }}>
                            <div className="min-w-0">
                                <div className="font-semibold text-slate-900">
                                    {new Date(e.date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
                                </div>
                                <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-3 mt-0.5">
                                    <span className="inline-flex items-center gap-1"><Thermometer className="h-3 w-3" /> Min {fmt(e.min_temp, 1)}° at {fmtTime(e.min_time)}</span>
                                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(e.start_time)}–{fmtTime(e.end_time)}</span>
                                </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <div className="fw-metric font-bold" style={{ color: c.text }}>{fmtMinutes(e.minutes_below)}</div>
                                <div className="text-[10px] text-slate-500">below {threshold}°C</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
